import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import './HomePage.css';

// Images (keep your existing names + add the new ones)
import heroImg from './images/hero-futuristic-warehouse.jpg';
import warehouseImg from './images/warehouse.jpg';
import deliveryImg from './images/delivery.jpg';
import tspImg from './images/tsp.jpg';   // NEW for TSP
import mmoImg from './images/mmo.jpg';   // NEW for Multi-modal

export default function HomePage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  // Unique images per module, no duplicates
  const modules = [
    { name: 'Vehicle Routing Problem', path: '/vrp', img: deliveryImg, level: 'Intermediate' },
    { name: 'Travelling Salesman Problem', path: '/tsp', img: tspImg, level: 'Beginner' },
    { name: 'Transportation Problem', path: '/tp', img: warehouseImg, level: 'Intermediate' },
    { name: 'Multi-modal Transport Optimisation', path: '/multi-modal', img: mmoImg, level: 'Advanced' },
  ];

  const handleFaqClick = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="homepage-container">
      <NavBar />

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Master Logistics <br />
              Through <span className="hero-title-highlight">Interactive Simulations</span>
            </h1>
            <p className="hero-description">
              Discover, experiment, and learn fundamental logistics concepts using intuitive,
              real-time simulations designed for students.
            </p>
            <button className="logisim-button" onClick={() => navigate('/about')}>
              What is LogiSim?
            </button>
          </div>

          <div className="hero-image">
            <img src={heroImg} alt="Logistics Simulation" className="main-image" />

          </div>
        </div>
      </section>

      {/* Why Choose LogiSim */}
      <section className="why-section">
        <h2 className="section-title">Why Choose LogiSim?</h2>
        <p className="section-subtitle">Built for learners, driven by clarity, backed by logic.</p>

        <div className="features-grid">
          <div className="feature-card">
            <h3 className="feature-title">Step-by-Step Learning</h3>
            <p className="feature-description">
              Guided workflows and tooltips help students understand each logic step interactively.
            </p>
          </div>
          <div className="feature-card">
            <h3 className="feature-title">Algorithm Visuals</h3>
            <p className="feature-description">
              Graphical walkthroughs for TSP, VRP, and more — using optimal path visualizations.
            </p>
          </div>
          <div className="feature-card">
            <h3 className="feature-title">Built-in Comparisons</h3>
            <p className="feature-description">
              Compare your solution to optimal paths with real-time feedback and scoring.
            </p>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="modules-section full-width-section">
        <h2 className="section-title">Explore Our Modules</h2>
        <p className="section-subtitle">
          Interactive simulations designed for foundational and advanced learning.
        </p>

        <div className="modules-grid">
          {modules.map((m) => (
            <div
              key={m.name}
              className="module-card"
              onClick={() => navigate(m.path)}
              role="button"
              aria-label={`Open ${m.name}`}
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate(m.path)}
            >
              <div className="module-image-container">
                <img src={m.img} alt={m.name} className="module-image" loading="lazy" />
                <div className="mod-img-gradient" />
                {m.isNew && <span className="badge-new">New</span>}
              </div>

              <div className="module-info">
                <h3 className="module-title">{m.name}</h3>
                <div className="module-footer">
                  <span className="explore-cta">Explore →</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section full-width-section">
        <h2 className="section-title text-center">What Our Users Say</h2>

        <div className="testimonials-container">
          <div className="testimonial">
            <div className="testimonial-quote-line"></div>
            <p className="testimonial-text">
              "LogiSim helped me understand algorithms better than any textbook! I loved the real-time comparisons."
            </p>
            <p className="testimonial-author">— Uni Student, Transport & Logistics Major</p>
          </div>

          <div className="testimonial">
            <div className="testimonial-quote-line"></div>
            <p className="testimonial-text">
              "We use LogiSim in our tutorials now. It's intuitive and makes teaching easier."
            </p>
            <p className="testimonial-author">— Lecturer, Logistics & Optimization</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section full-width-section">
        <h2 className="section-title text-center">Frequently Asked Questions</h2>

        <div className="faq-container">
          <div className="faq-item" onClick={() => handleFaqClick(0)}>
            <div className="faq-question">
              <span className={`faq-arrow ${openFaq === 0 ? 'rotated' : ''}`}>▸</span>
              Do I need coding experience?
            </div>
            {openFaq === 0 && (
              <div className="faq-answer">
                Not at all. The platform is beginner-friendly with interactive guidance at each step.
              </div>
            )}
          </div>

          <div className="faq-item" onClick={() => handleFaqClick(1)}>
            <div className="faq-question">
              <span className={`faq-arrow ${openFaq === 1 ? 'rotated' : ''}`}>▸</span>
              Can I track my progress?
            </div>
            {openFaq === 1 && (
              <div className="faq-answer">
                Unfortunately, this feature is not yet available. Progress tracking will be developed once the user authentication system is established in future updates.
              </div>
            )}
          </div>

          <div className="faq-item" onClick={() => handleFaqClick(2)}>
            <div className="faq-question">
              <span className={`faq-arrow ${openFaq === 2 ? 'rotated' : ''}`}>▸</span>
              Is it mobile responsive?
            </div>
            {openFaq === 2 && (
              <div className="faq-answer">
                Mobile devices are not supported. The application requires a larger screen to display the extensive information, interactive maps, and detailed data visualizations effectively. Please use a desktop or laptop computer for the best experience.
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}